import {uniqBy} from '@shared/helpers/array'
import {includes} from '@shared/helpers/collection'
import {getDday} from '@shared/helpers/date'
import {contains} from '@shared/helpers/function'
import {isEmpty, gtZero} from '@shared/helpers/lang'
import {createStateMaker} from '@shared/helpers/object'

import {
    APIStatus,
    Card,
    CardCodeType,
    CardOwnerGroup,
    CardType,
    EasyCardItemType,
    InterestFreeInstallmentType,
    InterestFreeStatus,
    NonInstallment,
    PaymentsCardCode,
    PaymentsCardCodeType,
    PromotionCardCode,
    PromotionCardCodeType,
    PromotionCardProductNameCode,
    PromotionCardProductNameCodeType,
    PromotionType,
} from '@web/core/constants'
import {getCardCodeName} from '@web/helpers/policies'

import {CardState, CardView, GroupItem} from '../interfaces'
import {FreeInstallmentBenefit, InterestFreeInstallment, UsablePointByCardCorp} from '../interfaces/card'
import {GroupItem as NullableGroupItem} from '../interfaces/selector'

export interface InterestInstallment {
    /** 카드사 코드 */
    cardCompanyCode: string
    /** 카드사 명 */
    cardCompanyName: string
    /** 할부 개월 수 */
    installmentMonth: number
    /** 최소 적용 결제 금액 */
    interestMinimumAmount: number
}

export const getInitialPromotionInfo = ({
    cardCompanyCode,
    cardProductNameCode,
}: {
    cardCompanyCode: PromotionCardCodeType
    cardProductNameCode?: PromotionCardProductNameCodeType
}) => {
    return {
        card: {
            companyCode: cardCompanyCode,
            companyName: '',
            detailInfos: [],
            productNameCode: cardProductNameCode,
        },
        totalPromotionApplyAmount: 0,
        cardPromotions: [],
        type: cardProductNameCode ? PromotionType.CARD_PRODUCT : PromotionType.GENERAL,
    }
}

export const getInitialInterestFreeInfo = ({
    cardCompanyCode,
    installmentMonth,
    interestFreeType,
    minAmount,
    interestFreeMinimumAmount,
}: Pick<
    InterestFreeInstallment,
    'cardCompanyCode' | 'installmentMonth' | 'interestFreeType' | 'minAmount' | 'interestFreeMinimumAmount'
>): InterestFreeInstallment => ({
    interestBurdenType: '',
    interestBurdenTypeName: '',
    cardCompanyCode,
    cardCompanyName: `${getCardCodeName(cardCompanyCode as PaymentsCardCodeType)}카드`,
    customerBurdenMonths: [],
    installmentMonth,
    interestFreeType,
    interestFreeTypeName: '',
    merchantBurdenRatio: 0,
    minAmount,
    interestFreeMinimumAmount,
    applicableCardProductCode: null,
    interestFreeCodeType: null,
})

export const getInitialUsablePointByCardCorp = (): UsablePointByCardCorp => ({
    [PaymentsCardCode.BC]: false,
    [PaymentsCardCode.SHINHAN]: false,
    [PaymentsCardCode.KB]: false,
    [PaymentsCardCode.NH]: false,
    [PaymentsCardCode.LOTTE]: false,
    [PaymentsCardCode.SAMSUNG]: false,
    [PaymentsCardCode.CITY]: false,
    [PaymentsCardCode.WOORI]: false,
    [PaymentsCardCode.KAKAO]: false,
    [PaymentsCardCode.HYUNDAI]: false,
    [PaymentsCardCode.HANA]: false,
})

export const getInitialState = (): CardState => ({
    cardEasyPay: {
        items: [],
        interestFreeStatus: InterestFreeStatus.INSTALLMENT,
        interestFreeInstallments: [],
        interestFreeInstallmentsByCompanyCode: {},
        limitedInstallmentMonth: null,
        selected: {
            id: '',
            installment: NonInstallment,
            itemType: EasyCardItemType.ADD_CARD,
            usingCardCorpPoint: false,
        },
        billing: {
            pgCp: '',
            pgId: '',
            minimumAmount: null,
            maximumAmount: null,
        },
        usablePointByCardCorp: null,
        bannerItemTypes: [],
        payService: {
            payable: true,
            maintenance: null,
        },
    },
    creditCard: {
        availableTypes: [CardOwnerGroup.PRIVATE, CardOwnerGroup.CORPORATION],
        companies: [],
        interestFreeStatus: InterestFreeStatus.INSTALLMENT,
        interestFreeInstallments: [],
        interestFreeInstallmentsByCompanyCode: {},
        limitedInstallmentMonth: null,
        selected: {
            type: CardOwnerGroup.PRIVATE,
            companyCode: null,
            installment: NonInstallment,
            appCardInfo: null,
        },
        billing: {
            pgCp: '',
            pgId: '',
            sid: '',
            minimumAmount: null,
            maximumAmount: null,
        },
    },
    welfareCard: {
        companies: [],
        selected: {
            companyCode: null,
        },
        billing: {
            pgCp: '',
            pgId: '',
            minimumAmount: null,
            maximumAmount: null,
        },
    },
    creditCardBillDiscounts: [],
    partnership: {
        containsCreditCard: false,
        containsShinhanPartnershipCard: false,
        companyCodes: [],
    },
    promotion: {
        status: 'UNAVAILABLE',
        apiStatus: APIStatus.IDLE,
        selectedInfo: null,
        groups: [],
        skipInfo: {
            isSuggested: false,
            isConnectedEasyBank: false,
        },
    },
    commission: {
        [Card.CHECK]: 0,
        [Card.CREDIT]: 0,
        [Card.PREPAID]: 0,
        [Card.NFC_PREPAID]: 0,
        [Card.NONE]: 0,
    },
})

export const makeMock = createStateMaker<CardState>(getInitialState)
export const makeCardEasyPayMock = createStateMaker<CardState['cardEasyPay']>(() => getInitialState().cardEasyPay)

export const makeCreditCardMock = createStateMaker<CardState['creditCard']>(() => getInitialState().creditCard)

export const makeWelfareCardMock = createStateMaker<CardState['welfareCard']>(() => getInitialState().welfareCard)

/**
 * 카드사 무이자 타입 별 할부개월 목록
 * 빌링에서 내려오는 별도의 정렬기준을 통해 카드사 순서를 노출
 * https://oss.fin.navercorp.com/payOrder/order-qa/issues/3017#issuecomment-520387
 */
export const convertFreeInstallmentBenefit = (
    interestFreeInstallments: InterestFreeInstallment[],
): FreeInstallmentBenefit[] => {
    // 할부 정보를 기준으로 카드사 순서로 정렬 (중복제거)
    const result = uniqBy<InterestFreeInstallment>(
        ({cardCompanyCode}) => cardCompanyCode,
        interestFreeInstallments,
    ).map<FreeInstallmentBenefit>(({cardCompanyCode, cardCompanyName}) => ({
        cardCompanyCode,
        cardCompanyName,
        all: [],
        part: [],
    }))
    // 할부 정보를 정렬된 카드사에 반영
    interestFreeInstallments.forEach((interestFreeInstallment) => {
        const {applicableCardProductCode, interestFreeType, cardCompanyCode} = interestFreeInstallment
        // 상품 무이자 (롯데 18개월 등)의 경우 특정 카드상품코드에만 적용되므로, 할부내용은 미노출
        if (!applicableCardProductCode) {
            const index = result.findIndex(
                ({cardCompanyCode: resultCardCompanyCode}) => resultCardCompanyCode === cardCompanyCode,
            )
            if (index > -1) {
                switch (interestFreeType) {
                    case InterestFreeInstallmentType.ALL:
                        result[index]?.all.push(interestFreeInstallment)
                        break
                    case InterestFreeInstallmentType.PART:
                        result[index]?.part.push(interestFreeInstallment)
                        break
                }
            }
        }
    })
    // 할부 정보가 없는 카드사 제거 및 할부월 기준으로 정렬
    return result
        .filter(({all, part}) => !(isEmpty(all) && isEmpty(part)))
        .map((item) => {
            item.all.sort((a: InterestFreeInstallment, b: InterestFreeInstallment) =>
                a.installmentMonth < b.installmentMonth ? -1 : 1,
            )
            item.part.sort((a: InterestFreeInstallment, b: InterestFreeInstallment) =>
                a.installmentMonth < b.installmentMonth ? -1 : 1,
            )
            return item
        })
}

export const promotionUtil = {
    /** 프로모션 적용 가능한 간편카드인지 여부 반환 */
    isApplicableEasyCard: ({
        targetCard,
        promotionCardInfo,
    }: {
        targetCard: {
            companyCode: string
            type: Maybe<CardType>
            ownerType: Maybe<CardCodeType>
            issuerCode: Maybe<string>
            companyId: string
            productCode: Maybe<string>
        }
        promotionCardInfo: Omit<NullableGroupItem['card'], 'detailInfos'> & {
            detailInfos: Array<{
                companyId: string
                issuerCode: Maybe<string>
                types: Array<CardType>
                ownerTypes: Array<CardCodeType>
                productCodes: Maybe<Array<string>>
            }>
        }
    }) => {
        /**
         * 우리은행 프로모션인 경우만 예외처리
         * 간편카드의 companyCode가 우리 또는 BC일때 유효
         * 그 외에는 모두 프로모션의 companyCode와 간편카드의 companyCode가 일치해야함
         */
        const isValidCompanyCode = (() => {
            return (
                targetCard.companyCode === promotionCardInfo.companyCode ||
                (promotionCardInfo.companyCode === PromotionCardCode.WOORI &&
                    targetCard.companyCode === PromotionCardCode.BC)
            )
        })()

        /**
         * - 일반 프로모션인경우 promotion의 detailInfo.productCodes가 null
         * - 카드상품 프로모션인 경우에는 promotion의 detailInfo > productCodes가 타겟카드의 productCode를 포함하는지 체크 필요
         */
        return (
            isValidCompanyCode &&
            !!promotionCardInfo.detailInfos.find(
                (detailInfo) =>
                    (!detailInfo.productCodes ||
                        (targetCard.productCode && detailInfo.productCodes.includes(targetCard.productCode))) &&
                    contains(targetCard.type, detailInfo.types) &&
                    !!targetCard.ownerType &&
                    contains(targetCard.ownerType, detailInfo.ownerTypes) &&
                    targetCard.issuerCode === detailInfo.issuerCode &&
                    targetCard.companyId === detailInfo.companyId,
            )
        )
    },
    /**
     * 프로모션 그룹의 유효한 최소 결제 금액
     * - 중복 참여 제한이 없거나, 제한이 있을때 잔여 횟수가 1번 이상 남았을때
     */
    getValidMinPayAmount: (promotionGroup: NullableGroupItem) =>
        [...promotionGroup.cardPromotions]
            .filter(
                ({duplicateParticipation}) =>
                    !duplicateParticipation || gtZero(duplicateParticipation?.applicableRemainCount ?? 0),
            )
            .sort((promotionA, promotionB) => promotionA.minPayAmount - promotionB.minPayAmount)[0]?.minPayAmount ?? 0,
}

/** 간편카드 첫번째 카드 선택 로직 */
export const getCardEasyPaysFirstItem =
    <T extends object>(someCallback: (item: T) => boolean) =>
    (cardEasyPays: T[]): Maybe<T> => {
        return cardEasyPays.find((item) => someCallback(item)) ?? cardEasyPays[0] ?? null
    }

/** 간편카드 정렬 로직 */
export const sortCardEasyPaysItems =
    <T extends object>(sortCallback: (item: T) => boolean) =>
    (cardEasyPays: T[]): T[] => {
        // 첫번째 카드가 점검중이거나 가맹점에서 지원하지 않으면 순서 변경
        const index = cardEasyPays.findIndex((item) => sortCallback(item))
        return index < 0
            ? cardEasyPays
            : [
                  ...(cardEasyPays[index] ? [cardEasyPays[index]] : []),
                  ...cardEasyPays.slice(0, index),
                  ...cardEasyPays.slice(index + 1),
              ]
    }

/**
 * - 카드사별 앱카드 정보
 * - @CHECK 카드사별 앱카드 추가시 맵 형태로 변경
 */
export const getAppCardInfo = (cardCode: Maybe<PaymentsCardCodeType>): Maybe<{title: string}> => {
    if (cardCode === PaymentsCardCode.LOTTE) {
        return {title: '로카페이(앱카드)'}
    }

    return null
}

type OverseaCardEasyPay = {
    /** 카드사 코드 */
    cardCompanyCode: PaymentsCardCodeType
    /** 카드 아이디 */
    cardId: string
    /** 마스킹 카드 번호 */
    maskedCardNumber: string
    /** 카드 타입 CREDIT, CHECK, PREPAID */
    cardType: CardType
    /** 카드 발급사 코드 */
    cardIssuerCode: string
    /** 카드 상태 */
    overseasCardStatusType: 'ACTIVE' | 'SUSPENDED' | 'LEAVING' | 'DISPOSABLE'
    /** 카드 등록 만료일 */
    registrationExpiredDate: string
}

const OverseasCardBrandNameMap: Record<string, string> = {
    CDIF: 'Discover',
    CAMF: 'American Express',
    CVSF: 'Visa',
    CMCF: 'MasterCard',
    CJCF: 'JCB',
    CBCF: 'UnionPay',
}

const OverseaCardBiCodeNameMap: Record<string, string> = {
    CDIF: 'Discover',
    CAMF: 'amex',
    CVSF: 'visa',
    CMCF: 'master',
    CJCF: 'jcb',
}

export const convertOverseaEasyCard = (
    overseaCardEasyPays: OverseaCardEasyPay[],
): CardState['cardEasyPay']['items'] => {
    return overseaCardEasyPays.map((cardEasyPay) => {
        const companyCode = cardEasyPay.cardCompanyCode as PaymentsCardCodeType
        const [year, month, day] = cardEasyPay.registrationExpiredDate.split('-')
        /**
         * - overseasCardStatusType가 ACTIVE이고, '만료일이 지나지 않은 경우' ACTIVE
         *   - 만료일이 지나더라도, 카드사쪽에서 해외카드의 상태값을 일괄 변경하는 배치작업이 진행되기 전까지는 overseasCardStatusType가 ACTIVE로 내려오기 때문
         */
        const isPayableOverseasCard =
            cardEasyPay.overseasCardStatusType === 'ACTIVE' &&
            getDday({
                targetDay: `${year}-${month}-${day}`,
            }) > 0

        return {
            category: null,
            companyCode,
            companyName: OverseasCardBrandNameMap[cardEasyPay.cardIssuerCode] ?? '',
            companyId: '',
            id: cardEasyPay.cardId,
            imageUrl: '',
            name: OverseasCardBrandNameMap[cardEasyPay.cardIssuerCode] ?? '',
            ownerType: null,
            productCode: null,
            type: cardEasyPay.cardType ?? 'CREDIT',
            maskedNumber: cardEasyPay.maskedCardNumber,
            payService: {
                payable: isPayableOverseasCard,
                maintenance: null,
                supportedMerchant: false,
            },
            longTermInstallment: false,
            registeredDateTime: '',
            reward: null,
            interestFreeBenefitPromotion: {
                isAvailable: true,
                month: 0,
                minAmount: 0,
            },
            plcc: null,
            issuerCode: cardEasyPay.cardIssuerCode,
            baseInstallmentInfo: [],
            overseasCardStatusType: (() => {
                if (isPayableOverseasCard) {
                    return 'ACTIVE'
                } else if (
                    cardEasyPay.overseasCardStatusType === 'LEAVING' ||
                    cardEasyPay.overseasCardStatusType === 'DISPOSABLE'
                ) {
                    return cardEasyPay.overseasCardStatusType
                } else {
                    return 'SUSPENDED'
                }
            })(),
            registrationExpiredDate: cardEasyPay.registrationExpiredDate
                ? {
                      year: year ?? '',
                      month: month ?? '',
                      day: day ?? '',
                  }
                : undefined,

            biCodeName: OverseaCardBiCodeNameMap[cardEasyPay.cardIssuerCode] ?? '',
        }
    })
}

export const isCardProductPromotion = (code: string): code is PromotionCardProductNameCodeType => {
    return includes(code, Object.values(PromotionCardProductNameCode))
}

/**
 * slice의 optional field를 selector return type인 nullable로 바꾸기 위한 converter
 * api interface ~ slice interface 부분을 먼저 optional로 교체하고, 추후 사용처를 undefined로 교체한다.
 * selector에서 이 converter를 이용해 optional field를 nullable로 치환
 */
export const promotionGroupItemConverter = (groupItem: GroupItem): NullableGroupItem => {
    return {
        card: {
            companyCode: groupItem.card.companyCode,
            companyName: groupItem.card.companyName,
            detailInfos: groupItem.card.detailInfos
                ? groupItem.card.detailInfos.map((detailInfo) => {
                      return {
                          companyId: detailInfo.companyId,
                          issuerCode: detailInfo.issuerCode ?? null,
                          issuerName: detailInfo.issuerName ?? null,
                          types: detailInfo.types ?? [],
                          ownerTypes: detailInfo.ownerTypes ?? [],
                          productCodes: detailInfo.productCodes ?? null,
                      }
                  })
                : [],
            productName: groupItem.card.productName ?? null,
            productNameCode: groupItem.card.productNameCode ?? null,
        },
        cardPromotions: groupItem.cardPromotions
            ? groupItem.cardPromotions.map((cardPromotion) => {
                  return {
                      policyNo: cardPromotion.policyNo,
                      accumulationPolicySequence: cardPromotion.accumulationPolicySequence,
                      name: cardPromotion.name,
                      affiliateCard: cardPromotion.affiliateCard,
                      productNameCode: cardPromotion.productNameCode,
                      productName: cardPromotion.productName,
                      productCodes: cardPromotion.productCodes,
                      applyType: cardPromotion.applyType,
                      applyValue: cardPromotion.applyValue,
                      minPayAmount: cardPromotion.minPayAmount,
                      maxApplyAmount: cardPromotion.maxApplyAmount ?? null,
                      startDateTime: cardPromotion.startDateTime,
                      endDateTime: cardPromotion.endDateTime,
                      cautionText: cardPromotion.cautionText ?? null,
                      isRestrictedByMembership: cardPromotion.isRestrictedByMembership,
                      duplicateParticipation: cardPromotion.duplicateParticipation ?? null,
                      applicableProducts: cardPromotion.applicableProducts ?? [],
                      burdenInfos: cardPromotion.burdenInfos ?? [],
                      burdenBillingType: cardPromotion.burdenBillingType,
                  }
              })
            : [],
        totalPromotionApplyAmount: groupItem.totalPromotionApplyAmount,
        type: groupItem.type,
        unApplicableReason: groupItem.unApplicableReason ?? null,
        myOptimalDiscountPayAmount: groupItem.myOptimalDiscountPayAmount,
    }
}

/** 카드 목록에서 특정 productCode를 가진 카드가 있는지 여부 */
export const hasSpecificProductCodes = ({cardList, productCodes}: {cardList: CardView[]; productCodes: string[]}) =>
    !!cardList.find((card) => card.productCode && productCodes.includes(card.productCode))

/** 테스트용 CardView 객체 생성 헬퍼 함수 */
export const makeCardViewMock = (
    overrides: Partial<{
        companyCode: (typeof PaymentsCardCode)[keyof typeof PaymentsCardCode]
        companyName: string
        id: string
        name: string
        type: CardType
        productCode: string
        baseInstallmentInfo: Array<{month: number; minAmount: number}>
    }>,
): CardView => ({
    companyCode: PaymentsCardCode.BC,
    companyId: 'CCBC',
    companyName: 'BC카드',
    id: 'test-card-id',
    registeredDateTime: '2024-01-01T00:00:00Z',
    maskedNumber: '****-****-****-1234',
    imageUrl: '',
    type: 'CREDIT',
    name: '테스트 카드',
    ownerType: 'PRIVATE',
    productCode: 'TEST001',
    category: null,
    reward: null,
    plcc: null,
    interestFreeBenefitPromotion: {
        isAvailable: false,
        month: 0,
        minAmount: 0,
    },
    longTermInstallment: false,
    payService: {
        payable: true,
        maintenance: null,
        supportedMerchant: true,
    },
    issuerCode: null,
    baseInstallmentInfo: [
        {month: 1, minAmount: 0},
        {month: 3, minAmount: 50000},
    ],
    ...overrides,
})
